import { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import SalesTaxModal from 'components/SalesTaxModal';
import { ExtraServiceOrderStatusModal, ExtraServicePaymentAuthModal, ExtraServiceCreditCardFormModal, ExtraServicePayInMethodSelectionModal } from 'components/ExtraService';
import BlikCodeInputModal from 'components/BlikCodeInputModal';
import useAbTest from 'hooks/useAbTest';
import useTracking from 'hooks/useTracking/useTracking';
import useExtraServiceCardAuthRedirectResultEffect from 'hooks/useExtraServiceCardAuthRedirectResultEffect';
import { navigateToPage } from 'libs/utils/window';
import * as extraServiceSelectors from 'state/extra-service/selectors';
import { actions as extraServiceActions } from 'state/extra-service/slice';
import { ExtraServiceOrderStatus, ExtraServiceCheckoutModal, extraServiceOrderTypeToTrackingTypeMap } from 'constants/extra-service';
import { Screen } from 'constants/tracking/screens';
import { ClickableElement } from 'constants/tracking/clickable-elements';
import { clickEvent } from 'libs/common/event-tracker/events';
import useTrackViewExtraServiceCheckout from '../hooks';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";

const ExtraServiceCheckout = _ref => {
  let {
    show,
    onBack,
    orderId,
    orderType,
    entityType,
    orderPayable,
    onCheckoutFinish,
    addCardSourceParams,
    orderConfirmationModal,
    onInitStartAfterCardAuth,
    isOrderFree = false,
    shouldSelectPaymentMethodFirst = true
  } = _ref;
  const dispatch = useDispatch();
  const {
    track
  } = useTracking();
  const {
    0: cardAddResult,
    1: setCardAddResult
  } = useState(null);
  const errorMessage = useSelector(extraServiceSelectors.getPaymentErrorMessage);
  const orderStatus = useSelector(extraServiceSelectors.getPaymentStatus);
  const checkoutAuth = useSelector(extraServiceSelectors.getPaymentAuth);
  const selectedCreditCard = useSelector(extraServiceSelectors.getSelectedCreditCard);
  const creditCardPayInMethod = useSelector(extraServiceSelectors.getCreditCardPayInMethod);
  const activePayInMethod = useSelector(state => extraServiceSelectors.getActivePayInMethod(state, orderPayable === null || orderPayable === void 0 ? void 0 : orderPayable.amount));
  const isBlikAuthRequired = useSelector(state => extraServiceSelectors.getIsBlikAuthRequired(state, orderPayable === null || orderPayable === void 0 ? void 0 : orderPayable.amount));
  const {
    0: activeModal,
    1: setActiveModal
  } = useState(ExtraServiceCheckoutModal.None);
  useAbTest({
    abTestName: 'trustly_open_banking',
    shouldTrackExpose: activeModal === ExtraServiceCheckoutModal.OrderConfirmation
  });
  useTrackViewExtraServiceCheckout({
    shouldTrack: activeModal === ExtraServiceCheckoutModal.OrderConfirmation,
    orderId,
    orderType,
    screen: Screen.Checkout
  });
  useEffect(() => {
    dispatch(extraServiceActions.getPayInMethodsRequest());
  }, [dispatch]);
  useExtraServiceCardAuthRedirectResultEffect(orderType, {
    onSuccess: successResult => {
      onInitStartAfterCardAuth(successResult.orderId);
      setCardAddResult({
        isError: false,
        cardId: successResult.cardId
      });
    },
    onFailure: failureResult => {
      onInitStartAfterCardAuth(failureResult.orderId);
      setCardAddResult({
        isError: true
      });
    }
  });
  const handlePayInMethodSelectionConfirm = useCallback((selectedPayInMethod, creditCardId) => {
    dispatch(extraServiceActions.setSelectedPayInMethod({
      creditCardId,
      selectedPayInMethodId: selectedPayInMethod.id
    }));
    setActiveModal(ExtraServiceCheckoutModal.OrderConfirmation);
  }, [dispatch, setActiveModal]);
  useEffect(() => {
    if (!show) {
      setActiveModal(ExtraServiceCheckoutModal.None);
      return;
    }

    if (cardAddResult !== null && cardAddResult !== void 0 && cardAddResult.cardId && creditCardPayInMethod) {
      handlePayInMethodSelectionConfirm(creditCardPayInMethod, cardAddResult.cardId);
      return;
    }

    if (cardAddResult !== null && cardAddResult !== void 0 && cardAddResult.isError) {
      setActiveModal(ExtraServiceCheckoutModal.CreditCardForm);
      return;
    }

    if (shouldSelectPaymentMethodFirst && !activePayInMethod && !isOrderFree) {
      setActiveModal(ExtraServiceCheckoutModal.PayInMethodSelection);
      return;
    }

    setActiveModal(ExtraServiceCheckoutModal.OrderConfirmation);
  }, [show, orderId, dispatch, isOrderFree, cardAddResult, activePayInMethod, creditCardPayInMethod, shouldSelectPaymentMethodFirst, handlePayInMethodSelectionConfirm]);
  useEffect(() => {
    function handlePaymentAuth() {
      const {
        embedded,
        payInRedirectUrl
      } = checkoutAuth;
      if (!show || activeModal === ExtraServiceCheckoutModal.None) return;

      if (embedded) {
        setActiveModal(ExtraServiceCheckoutModal.PaymentAuth);
        return;
      }

      if (payInRedirectUrl) {
        navigateToPage(payInRedirectUrl);
      }
    }

    handlePaymentAuth();
  }, [show, activeModal, checkoutAuth, setActiveModal]);

  const confirmOrder = blikCode => {
    if (!orderId) return;
    track(clickEvent({
      target: ClickableElement.CheckoutPayButton,
      targetDetails: JSON.stringify({
        valid: true,
        service_order_id: orderId,
        type: extraServiceOrderTypeToTrackingTypeMap[orderType],
        payment_method: (activePayInMethod === null || activePayInMethod === void 0 ? void 0 : activePayInMethod.code.toLowerCase()) || null
      })
    }));

    if (isBlikAuthRequired && !blikCode) {
      setActiveModal(ExtraServiceCheckoutModal.BlikCodeInput);
      return;
    }

    dispatch(extraServiceActions.confirmOrderPaymentRequest({
      orderId,
      blikCode,
      orderType,
      entityType,
      selectedCreditCardId: selectedCreditCard === null || selectedCreditCard === void 0 ? void 0 : selectedCreditCard.id,
      selectedPaymentMethodId: activePayInMethod === null || activePayInMethod === void 0 ? void 0 : activePayInMethod.id
    }));
    setActiveModal(ExtraServiceCheckoutModal.OrderStatus);
  }; // TODO: get rid of this callbacks by refining child component callback arguments.


  const handleConfirmOrder = () => confirmOrder();

  const handleBlikCodeInput = blikCode => confirmOrder(blikCode);

  function handleCreditCardAdd(creditCard) {
    if (!creditCardPayInMethod) return;
    dispatch(extraServiceActions.addCreditCardSuccess({
      creditCard
    }));
    handlePayInMethodSelectionConfirm(creditCardPayInMethod, creditCard.id);
  }

  function openSalesTaxModal() {
    setActiveModal(ExtraServiceCheckoutModal.SalesTaxInfo);
  }

  function openOrderConfirmationModal() {
    setActiveModal(ExtraServiceCheckoutModal.OrderConfirmation);
  }

  function openPayinMethodSelectionModal() {
    setActiveModal(ExtraServiceCheckoutModal.PayInMethodSelection);
    setCardAddResult(null);
  }

  function openCreditCardModal() {
    setActiveModal(ExtraServiceCheckoutModal.CreditCardForm);
  }

  function closeCheckoutModal() {
    setActiveModal(ExtraServiceCheckoutModal.None);
    dispatch(extraServiceActions.cancelOrderPaymentAuth());
    onCheckoutFinish(orderStatus === ExtraServiceOrderStatus.Success);
  }

  function handle3DS2AuthSubmit(state) {
    if (!checkoutAuth.embedded) return;
    dispatch(extraServiceActions.updateOrderPaymentDataRequest({
      state,
      paymentId: checkoutAuth.embedded.paymentId
    }));
    setActiveModal(ExtraServiceCheckoutModal.OrderStatus);
  }

  function handle3DS2Error(paymentAuthErrorMessage) {
    dispatch(extraServiceActions.setOrderPaymentErrorMessage({
      errorMessage: paymentAuthErrorMessage
    }));
    setActiveModal(ExtraServiceCheckoutModal.OrderStatus);
  }

  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(ExtraServicePayInMethodSelectionModal, {
      show: activeModal === ExtraServiceCheckoutModal.PayInMethodSelection,
      orderId: orderId,
      orderType: orderType,
      orderPayable: orderPayable,
      selectedCreditCardId: selectedCreditCard === null || selectedCreditCard === void 0 ? void 0 : selectedCreditCard.id,
      selectedPayInMethodId: activePayInMethod === null || activePayInMethod === void 0 ? void 0 : activePayInMethod.id,
      onBack: onBack,
      onCancel: closeCheckoutModal,
      onAddCreditCard: openCreditCardModal,
      onConfirm: handlePayInMethodSelectionConfirm
    }), /*#__PURE__*/_jsx(ExtraServiceCreditCardFormModal, {
      show: activeModal === ExtraServiceCheckoutModal.CreditCardForm,
      orderId: orderId,
      orderType: orderType,
      isCardAddError: cardAddResult === null || cardAddResult === void 0 ? void 0 : cardAddResult.isError,
      addCardSourceParams: addCardSourceParams,
      onSuccess: handleCreditCardAdd,
      onBack: openPayinMethodSelectionModal
    }), /*#__PURE__*/_jsx(ExtraServicePaymentAuthModal, {
      show: activeModal === ExtraServiceCheckoutModal.PaymentAuth,
      onError: handle3DS2Error,
      onCancel: closeCheckoutModal,
      onAuthSubmitted: handle3DS2AuthSubmit
    }), /*#__PURE__*/_jsx(SalesTaxModal, {
      show: activeModal === ExtraServiceCheckoutModal.SalesTaxInfo,
      messageType: "extra_service",
      onClose: openOrderConfirmationModal
    }), /*#__PURE__*/_jsx(BlikCodeInputModal, {
      show: activeModal === ExtraServiceCheckoutModal.BlikCodeInput,
      onInputConfirmed: handleBlikCodeInput,
      onCancel: openOrderConfirmationModal
    }), /*#__PURE__*/_jsx(ExtraServiceOrderStatusModal, {
      show: activeModal === ExtraServiceCheckoutModal.OrderStatus,
      orderId: orderId,
      orderType: orderType,
      orderStatus: orderStatus,
      orderPayable: orderPayable,
      errorMessage: errorMessage,
      onContinue: closeCheckoutModal
    }), orderConfirmationModal({
      show: activeModal === ExtraServiceCheckoutModal.OrderConfirmation,
      onBack,
      onConfirmOrder: handleConfirmOrder,
      onShowSalesTaxModal: openSalesTaxModal,
      onPaymentMethodSelect: openPayinMethodSelectionModal
    })]
  });
};

export default ExtraServiceCheckout;